import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {GetAllCategory} from "../../action";
import {GenderData} from '../../action';
import {useNavigate} from "react-router-dom";
import axios from "../../helper/axios";
import genderImg from '../../assets/female-choose.jpg';
import BrandSlider from "../../component/BrandFocusSlider/BrandSlider";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination, Autoplay} from "swiper";

const brandsContext = require.context(
    "../../assets/LandingPageSlider",
    true,
    /\.(png|jpe?g|svg|webp)$/
);

function getAllBrandImages() {
    try {
        const keys = brandsContext.keys().sort();
        return keys.map((k) => {
            const mod = brandsContext(k);
            return mod && mod.default ? mod.default : mod;
        });
    } catch (err) {
        console.warn("Error loading images from LandingPageSlider", err);
        return [];
    }
}

/**
 * Props:
 *  - chooseGender: function (required) -> called with "male" or "female"
 *  - v2: optional fallback image url/string
 */
export default function SectionImageSlider({v2}) {
    const [swiper, setSwiper] = useState(null);
    const navigate = useNavigate();
    const placeholder = v2 || "https://placehold.co/1600x400/cccccc/cccccc.png";

    const initialImages = getAllBrandImages();
    const [banners] = useState(initialImages.length > 0 ? initialImages : [placeholder]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [imgError, setImgError] = useState(() => new Array(banners.length).fill(false));
    const [isTransitioning, setIsTransitioning] = useState(true);

    const genderData = useSelector(state => state.category?.gender);
    const dispatch = useDispatch();

    const chooseGender = async (gender) => {
        await dispatch(GenderData(gender))
        const data = {genderBased: gender}
        await dispatch(GetAllCategory(data));
        navigate("/" + gender);
    }


    const containerRef = useRef(null);
    const sliderRef = useRef(null);
    const autoSlideTimerRef = useRef(null);
    const resizeObserverRef = useRef(null);

    // keep imgError length in sync if banners length changes
    useEffect(() => {
        setImgError((prev) => {
            const next = new Array(banners.length).fill(false);
            for (let i = 0; i < Math.min(prev.length, next.length); i++) next[i] = prev[i];
            return next;
        });
    }, [banners.length]);

    // Auto-slide: start on mount and when banners length changes
    useEffect(() => {
        startAutoSlide();
        return () => {
            clearInterval(autoSlideTimerRef.current);
            if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [banners.length]);

    function startAutoSlide() {
        clearInterval(autoSlideTimerRef.current);
        autoSlideTimerRef.current = setInterval(() => {
            setCurrentIndex((i) => (i + 1) % banners.length);
        }, 6000);
    }

    const goTo = (index) => {
        setIsTransitioning(true);
        setCurrentIndex((index + banners.length) % banners.length);
        startAutoSlide(); // restart timer on manual navigation
    };

    const goPrev = () => goTo(currentIndex - 1);
    const goNext = () => goTo(currentIndex + 1);

    // apply transform when index or transition flag changes
    useEffect(() => {
        const container = containerRef.current;
        const slider = sliderRef.current;
        if (!container || !slider) return;

        const width = container.clientWidth || 1;
        slider.style.transition = isTransitioning ? "transform 500ms ease" : "none";
        slider.style.transform = `translateX(${-currentIndex * width}px)`;
    }, [currentIndex, isTransitioning, banners.length]);

    // resize handling (ResizeObserver preferred)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const applyTransformOnResize = () => {
            // temporarily disable transitions to prevent visual jumpiness
            setIsTransitioning(false);
            // re-enable transitions after a couple of frames
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setIsTransitioning(true));
            });
        };

        if (typeof ResizeObserver !== "undefined") {
            resizeObserverRef.current = new ResizeObserver(applyTransformOnResize);
            resizeObserverRef.current.observe(container);
            return () => {
                if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
            };
        }

        // fallback to window resize
        window.addEventListener("resize", applyTransformOnResize);
        return () => window.removeEventListener("resize", applyTransformOnResize);
    }, []);

    const handleImgError = (index) => {
        setImgError((prev) => {
            const next = [...prev];
            next[index] = true;
            return next;
        });
    };

    // Banner Config State
    const [activeBanner, setActiveBanner] = useState(null);
    const [bannerLoading, setBannerLoading] = useState(true);
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const bannerSwiperRef = useRef(null);
    const bannerAutoPlayRef = useRef(null);

    const normalizeBannerUrl = (config) => {
        if (!config || !config.bannerUrl) return config;
        const c = { ...config };
        if (c.bannerUrl.includes('localhost')) {
            const urlPath = c.bannerUrl.split('/bannerUploads/')[1];
            if (urlPath) {
                const baseUrl = process.env.REACT_APP_BASE_URL || '';
                const cleanBaseUrl = baseUrl.replace('/api', '').replace(/\/$/, '');
                c.bannerUrl = cleanBaseUrl + '/bannerUploads/' + urlPath;
            }
        } else if (!c.bannerUrl.startsWith('http')) {
            const baseUrl = process.env.REACT_APP_BASE_URL || '';
            const cleanBaseUrl = baseUrl.replace('/api', '').replace(/\/$/, '');
            c.bannerUrl = cleanBaseUrl + (c.bannerUrl.startsWith('/') ? '' : '/') + c.bannerUrl;
        }
        return c;
    };

    // Fetch active banners nga API (mund të keni 2, 3 ose më shumë banera aktivë)
    useEffect(() => {
        const fetchActiveBanner = async () => {
            try {
                setBannerLoading(true);
                const res = await axios.get("/getactivebanner");
                if (res.status === 200 && res.data.isActive && res.data.configs && res.data.configs.length > 0) {
                    const configs = res.data.configs.map(normalizeBannerUrl);
                    setActiveBanner(configs);
                } else if (res.status === 200 && res.data.isActive && res.data.config) {
                    setActiveBanner([normalizeBannerUrl(res.data.config)]);
                } else {
                    setActiveBanner(null);
                }
            } catch (error) {
                console.error("Error fetching active banner:", error);
                setActiveBanner(null);
            } finally {
                setBannerLoading(false);
            }
        };

        fetchActiveBanner();
        // Refresh çdo 5 minuta për të kontrolluar nëse ka baner të ri
        const interval = setInterval(fetchActiveBanner, 5 * 60 * 1000);
        return () => {
            clearInterval(interval);
            if (bannerAutoPlayRef.current) {
                clearInterval(bannerAutoPlayRef.current);
            }
        };
    }, []);

    // Collaborators / Influencers nga API (menaxhohen nga admin paneli)
    const [collaboratorsList, setCollaboratorsList] = useState([]);

    useEffect(() => {
        const fetchCollaborators = async () => {
            try {
                const res = await axios.get("/getcollaborators");
                if (res.status === 200 && Array.isArray(res.data)) {
                    setCollaboratorsList(
                        res.data.map((c) => ({
                            name: c.description || "collaborator",
                            image: c.image,
                            link: c.link
                        }))
                    );
                }
            } catch (error) {
                console.warn("Error fetching collaborators:", error);
            }
        };
        fetchCollaborators();
    }, []);


    const onBannerClick = (config) => {
        if (config && config.targetUrl) {
            navigate(config.targetUrl);
        } else {
            navigate('/products?discount=yes');
        }
    };

    const activeBannerList = Array.isArray(activeBanner) ? activeBanner : (activeBanner ? [activeBanner] : []);

    const startBannerAutoplay = (swiper) => {
        if (!swiper || swiper.destroyed || activeBannerList.length <= 1) return;
        if (bannerAutoPlayRef.current) {
            clearInterval(bannerAutoPlayRef.current);
        }
        bannerAutoPlayRef.current = setInterval(() => {
            if (!swiper || swiper.destroyed) return;
            const total = activeBannerList.length || 1;
            const current = typeof swiper.activeIndex === "number" ? swiper.activeIndex : 0;
            const next = (current + 1) % total;
            swiper.slideTo(next, 1000);
        }, 5000);
    };

    const handlePrevBanner = () => {
        const swiper = bannerSwiperRef.current;
        if (!swiper || swiper.destroyed || activeBannerList.length === 0) return;
        const total = activeBannerList.length;
        const current = typeof swiper.activeIndex === "number" ? swiper.activeIndex : 0;
        const prev = (current - 1 + total) % total;
        swiper.slideTo(prev, 500);
        startBannerAutoplay(swiper);
    };

    const handleNextBanner = () => {
        const swiper = bannerSwiperRef.current;
        if (!swiper || swiper.destroyed || activeBannerList.length === 0) return;
        const total = activeBannerList.length;
        const current = typeof swiper.activeIndex === "number" ? swiper.activeIndex : 0;
        const next = (current + 1) % total;
        swiper.slideTo(next, 500);
        startBannerAutoplay(swiper);
    };

    // Swiper's own drag can't be used: the fade CSS on .main-banner-swiper pins the
    // wrapper translate to 0, which breaks Swiper's snap math after the 2nd slide.
    const bannerTouchRef = useRef(null);

    const handleBannerTouchStart = (e) => {
        const t = e.touches[0];
        bannerTouchRef.current = { x: t.clientX, y: t.clientY };
    };

    const handleBannerTouchEnd = (e) => {
        const start = bannerTouchRef.current;
        bannerTouchRef.current = null;
        if (!start || activeBannerList.length <= 1) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) handleNextBanner();
        else handlePrevBanner();
    };

    return (
        <div className="section-image-slider">
            <div className="container mx-auto px-4">

                {/* Blloku i banerit – shfaqet vetëm kur ka banera; nëse nuk ka, pjesa male/female ngjitet lart */}
                {!bannerLoading && activeBannerList.length > 0 && (
                <div
                    style={{ position: 'relative', touchAction: 'pan-y' }}
                    onTouchStart={handleBannerTouchStart}
                    onTouchEnd={handleBannerTouchEnd}
                >
                    <Swiper
                        modules={[Pagination]}
                        speed={1000}
                        slidesPerView={1}
                        pagination={{ clickable: true }}
                        navigation={false}
                        loop={false}
                        allowTouchMove={false}
                        initialSlide={0}
                        className="main-banner-swiper"
                        onSwiper={(swiper) => {
                            bannerSwiperRef.current = swiper;
                            setCurrentBannerIndex(swiper.activeIndex || 0);
                            startBannerAutoplay(swiper);
                        }}
                        onSlideChange={(swiper) => {
                            setCurrentBannerIndex(swiper.activeIndex || 0);
                            // Reset autoplay timer so each banner always gets a full 5s
                            startBannerAutoplay(swiper);
                        }}
                >
                
                    {/* Të gjithë banerat vijnë nga admin panel – nuk ka më baner të fiksuar në kod */}
                    {activeBannerList.map((config, idx) => (
                        <SwiperSlide key={config._id || idx}>
                            <div
                                className="black-friday-banner-image"
                                role="button"
                                tabIndex={0}
                                aria-label="Campaign banner"
                                onClick={() => onBannerClick(config)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') onBannerClick(config);
                                }}
                            >
                                {config.bannerType === "video" ? (
                                    <video 
                                        src={config.bannerUrl} 
                                        muted 
                                        autoPlay 
                                        loop 
                                        playsInline
                                        className="black-friday-banner-video"
                                        poster={config.bannerUrl}
                                    >
                                        <img src={config.bannerUrl} alt="Campaign Banner" />
                                    </video>
                                ) : (
                                    <img src={config.bannerUrl} alt="Campaign Banner" />
                                )}
                            </div>
                        </SwiperSlide>
                    ))}

                    </Swiper>
                    {/* 2 butona navigimi (prev/next) që punojnë dinamikisht, pavarësisht sa banera ka */}
                    <button
                        type="button"
                        className="main-banner-swiper-nav main-banner-swiper-nav--prev"
                        aria-label="Baneri paraprak"
                        onClick={handlePrevBanner}
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className="main-banner-swiper-nav main-banner-swiper-nav--next"
                        aria-label="Baneri tjetër"
                        onClick={handleNextBanner}
                    >
                        ›
                    </button>
                </div>
                )}

                <section className="intro-brands intro-brands--premium intro-brands--after-banner mt-6">
                    <div className="intro-brands__header">
                        <div className="intro-brands__title">
                            Brendet
                        </div>
                        <button
                            type="button"
                            className="intro-brands__cta"
                            onClick={() => navigate('/brands')}
                        >
                            Shiko të gjitha 
                        </button>
                    </div>

                    <div className="intro-brands__slider">
                        <BrandSlider/>
                    </div>
                </section>



                

                {/* ============================================ */}
                {/* PJESA E BANERIT PER BLACK FRIDAY - DINAMIK */}
                {/* ============================================ */}
                {/* Komentuar - po përdorim video statik në vend të banerit dinamik nga API */}


                {/* PJESA E BANERIT PER GENDER CHOICE */}

                <div className={'gender-choose-wrapper mt-6'}>
                <div className="gender-choose" style={{ '--gender-photo': `url(${genderImg})` }}>
                    <img className="gender-choose-photo" src={genderImg} alt="Zgjidh veshje për femër ose mashkull"/>
                    <button
                        type="button"
                        className="gender-choose-hitzone gender-choose-hitzone--female"
                        aria-label="Zgjidh veshje për femër"
                        onClick={() => chooseGender("female")}
                    >
                        <img className="gender-choose-half" src={genderImg} alt=""/>
                        <span className="gender-choose-label">FEMËR</span>
                    </button>
                    <button
                        type="button"
                        className="gender-choose-hitzone gender-choose-hitzone--male"
                        aria-label="Zgjidh veshje për mashkull"
                        onClick={() => chooseGender("male")}
                    >
                        <img className="gender-choose-half" src={genderImg} alt=""/>
                        <span className="gender-choose-label">MASHKULL</span>
                    </button>
                </div>
            </div>
            </div>

                        <br/>

            <div className={'intro-brands'}>
                <h4 className={'text-center  text-2xl font-semibold mb-2 mt-4'}>
                    Albi Online ju sjell stil nga kryeqendrat e modës, me mbi <strong>50 brende</strong> botërore <br/>dhe më shumë se <strong>50 mijë artikuj</strong> nga koleksionet më të fundit.
                    <br/>
                    <br/>
                    <br/>
                    <br/>
                </h4>
            </div>
                        
                        
            

            {collaboratorsList.length > 0 && (
            <div className="collaborators"
                 >
                <div className="px-4">

                    <h4>#influencers</h4>
                    <h5>Vizitoni <a href="https://www.instagram.com/albionline"  rel="noopener noreferrer" target="_blank">@albionline</a> ne
                        instagram</h5>
                    <Swiper
                        slidesPerView={7}
                        spaceBetween={30}
                        loop={collaboratorsList.length > 7}
                        navigation={false}
                        autoplay={collaboratorsList.length > 7 ? {
                            delay: 2000,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true
                        } : false}
                        breakpoints={{
                            0: { slidesPerView: 2, spaceBetween: 20 },
                            480: { slidesPerView: 2, spaceBetween: 20 },
                            640: { slidesPerView: 3, spaceBetween: 20 },
                            768: { slidesPerView: 4, spaceBetween: 20 },
                            1024: { slidesPerView: 5, spaceBetween: 30 },
                            1280: { slidesPerView: 6, spaceBetween: 30 },
                            1536: { slidesPerView: 7, spaceBetween: 30 },
                            
                        }}
                        modules={[Pagination, Autoplay]}
                        className="mySwiper"
                    >
                        {collaboratorsList.map((item, index) => {
                            return (
                                <SwiperSlide key={index}>
                                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="collaborator-item">
                                        <img src={item.image} alt={item.name}/>
                                    </a>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </div>
            )}

            {/* CSS për butonat e navigimit dhe pagination */}
            <style>{`
                /* Pagination - në mes, si pika të thjeshta */
                .main-banner-swiper .swiper-pagination,
                .main-banner-swiper .swiper-pagination-clickable,
                .main-banner-swiper .swiper-pagination-bullets,
                .main-banner-swiper .swiper-pagination-horizontal {
                    position: absolute !important;
                    bottom: 35px !important;
                    left: 50% !important;
                    right: auto !important;
                    transform: translateX(-50%) !important;
                    width: auto !important;
                    display: flex !important;
                    justify-content: center !important;
                    align-items: center !important;
                    gap: 10px;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                
                .main-banner-swiper .swiper-pagination-bullet {
                    width: 12px !important;
                    height: 12px !important;
                    border-radius: 50% !important;
                    background: rgba(255, 255, 255, 0.5) !important;
                    opacity: 1 !important;
                    margin: 0 5px !important;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: none !important;
                    padding: 0 !important;
                }
                
                .main-banner-swiper .swiper-pagination-bullet-active {
                    background: rgba(255, 255, 255, 1) !important;
                    width: 12px !important;
                    height: 12px !important;
                    border-radius: 50% !important;
                }
                
                .main-banner-swiper .swiper-pagination-bullet:hover {
                    background: rgba(255, 255, 255, 0.8) !important;
                }
                
                /* Mobile pagination */
                @media (max-width: 768px) {
                    .main-banner-swiper .swiper-pagination,
                    .main-banner-swiper .swiper-pagination-clickable,
                    .main-banner-swiper .swiper-pagination-bullets,
                    .main-banner-swiper .swiper-pagination-horizontal {
                        bottom: 25px !important;
                        gap: 8px;
                    }
                    
                    .main-banner-swiper .swiper-pagination-bullet {
                        width: 10px !important;
                        height: 10px !important;
                        margin: 0 4px !important;
                    }
                    
                    .main-banner-swiper .swiper-pagination-bullet-active {
                        width: 10px !important;
                        height: 10px !important;
                    }
                    
                    /* Siguro që të gjitha baneret kanë të njëjtat dimensione në mobile */
                    .main-banner-swiper .black-friday-banner-image img,
                    .main-banner-swiper .black-friday-banner-image video,
                    .main-banner-swiper .black-friday-banner-video {
                        width: 100% !important;
                        height: auto !important;
                        object-fit: cover !important;
                        display: block !important;
                    }
                }

                /* Butonat prev/next të banerit kryesor */
                .main-banner-swiper-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                    width: 36px;
                    height: 36px;
                    border-radius: 999px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255, 255, 255, 0.85);
                    color: #111;
                    cursor: pointer;
                    transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
                    font-size: 18px;
                    line-height: 1;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
                }

                .main-banner-swiper-nav--prev {
                    left: 20px;
                }

                .main-banner-swiper-nav--next {
                    right: 20px;
                }

                .main-banner-swiper-nav:hover {
                    background: #ffffff;
                    transform: translateY(-50%) scale(1.06);
                    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.25);
                }

                @media (max-width: 768px) {
                    .main-banner-swiper-nav {
                        width: 30px;
                        height: 30px;
                        font-size: 16px;
                        box-shadow: 0 5px 16px rgba(0, 0, 0, 0.18);
                    }

                    .main-banner-swiper-nav--prev {
                        left: 0px;
                    }

                    .main-banner-swiper-nav--next {
                        right: 0px;
                    }
                }
            `}</style>
        </div>
    );
}


